/**
 * AlertTrendsView Component
 *
 * Displays alert trends data as a line chart.
 * Part of the Analytics Card split for better maintainability.
 *
 * NOTE: polaris-viz is NOT SSR-compatible (uses useLayoutEffect and window).
 * The LineChart is wrapped with ClientOnly to prevent hydration errors.
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 2.2
 */

import { LineChart } from '@shopify/polaris-viz';
import { ChartLineIcon } from '@shopify/polaris-icons';
import { SkeletonBodyText, Box } from '@shopify/polaris';
import type { ChartDataPoint } from '../../../types/dashboard';
import { TabEmptyState } from '../TabEmptyState';
import { ClientOnly } from '~/components/core/ClientOnly';

/**
 * Polaris semantic colors for alert severity
 */
const CHART_COLORS = {
  critical: '#D72C0D', // Red - critical alerts
  warning: '#B98900',  // Orange/Yellow - warning alerts
  info: '#2C6ECB',     // Blue - info alerts
};

/**
 * Props for AlertTrendsView
 */
interface AlertTrendsViewProps {
  /** Alert trends data points */
  data: ChartDataPoint[];
}

/**
 * Transform raw alert trends data into polaris-viz format
 */
function transformAlertTrendsData(data: ChartDataPoint[]) {
  return [
    {
      name: 'Critical',
      data: data.map(d => ({ key: d.date, value: d.critical })),
      color: CHART_COLORS.critical,
    },
    {
      name: 'Warning',
      data: data.map(d => ({ key: d.date, value: d.warning })),
      color: CHART_COLORS.warning,
    },
    {
      name: 'Info',
      data: data.map(d => ({ key: d.date, value: d.info })),
      color: CHART_COLORS.info,
    },
  ];
}

/**
 * Alert Trends View
 * Displays a line chart showing alert counts by severity over time
 */
export function AlertTrendsView({ data }: AlertTrendsViewProps) {
  const hasData = data && data.length > 0;

  if (!hasData) {
    return (
      <div className="empty-state-section">
        <div className="analytics-content-enter">
          <TabEmptyState
            icon={ChartLineIcon}
            title="No alert data available"
            description="Alerts will appear here once inventory thresholds are triggered"
          />
        </div>
      </div>
    );
  }

  const vizData = transformAlertTrendsData(data);

  return (
    <div className="chart-content-enter">
      <ClientOnly
        fallback={
          <Box padding="400">
            <SkeletonBodyText lines={6} />
          </Box>
        }
      >
        {() => (
          <LineChart
            data={vizData}
            theme="Light"
            showLegend={true}
            isAnimated={true}
            yAxisOptions={{
              labelFormatter: (value) => Math.round(Number(value)).toString(),
            }}
          />
        )}
      </ClientOnly>
    </div>
  );
}
